'use client';

import React, { createContext, useContext, useState, useEffect, useRef, useCallback, ReactNode } from 'react';
import LoadingScreen from '@/components/LoadingScreen';

interface LoadingContextType {
  isLoading: boolean;
  startLoading: () => void;
  stopLoading: () => void;
  showLoadingScreen: (loading: boolean) => void;
  setLoadingWithDelay: (loading: boolean, delay?: number) => void;
}

const LoadingContext = createContext<LoadingContextType | undefined>(undefined);

export const useLoading = () => {
  const context = useContext(LoadingContext);
  if (!context) {
    throw new Error('useLoading must be used within a LoadingProvider');
  }
  return context;
};

export const LoadingProvider = ({ children }: { children: ReactNode }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [showFullScreenLoading, setShowFullScreenLoading] = useState(false);
  const loadingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const safetyTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const loadingStartTimeRef = useRef<number | null>(null);
  const MIN_LOADING_TIME = 800;

  // Automatycznie ukryj ekran ładowania po załadowaniu strony
  useEffect(() => {
    const handleLoad = () => {
      const timer = setTimeout(() => {
        setIsLoading(false);
        setShowFullScreenLoading(false);
      }, 300);

      return () => clearTimeout(timer);
    };

    if (document.readyState === 'complete') {
      handleLoad();
    } else {
      window.addEventListener('load', handleLoad);
      return () => {
        window.removeEventListener('load', handleLoad);
      };
    }
  }, []);

  // popstate listener removed - was causing fullscreen overlay on every back/forward navigation

  // Safety timeout - 3s max instead of 8s
  useEffect(() => {
    if (isLoading) {
      if (safetyTimeoutRef.current) {
        clearTimeout(safetyTimeoutRef.current);
      }

      safetyTimeoutRef.current = setTimeout(() => {
        setIsLoading(false);
        setShowFullScreenLoading(false);
        loadingStartTimeRef.current = null;

        try {
          sessionStorage.removeItem('navigatingFromEvent');
          sessionStorage.removeItem('navigatingTime');
          sessionStorage.removeItem('directTrainingNavigation');
        } catch (error) {
          // ignore
        }
      }, 3000);
    } else {
      if (safetyTimeoutRef.current) {
        clearTimeout(safetyTimeoutRef.current);
        safetyTimeoutRef.current = null;
      }
    }

    return () => {
      if (safetyTimeoutRef.current) {
        clearTimeout(safetyTimeoutRef.current);
        safetyTimeoutRef.current = null;
      }
    };
  }, [isLoading]);

  const ensureMinLoadingTime = useCallback((callback: () => void) => {
    if (loadingStartTimeRef.current === null) {
      callback();
      return;
    }

    const elapsedTime = Date.now() - loadingStartTimeRef.current;

    if (elapsedTime >= MIN_LOADING_TIME) {
      callback();
    } else {
      const remainingTime = MIN_LOADING_TIME - elapsedTime;
      if (loadingTimeoutRef.current) {
        clearTimeout(loadingTimeoutRef.current);
      }
      loadingTimeoutRef.current = setTimeout(() => {
        callback();
        loadingTimeoutRef.current = null;
      }, remainingTime);
    }
  }, []);

  const startLoading = useCallback(() => {
    if (loadingTimeoutRef.current) {
      clearTimeout(loadingTimeoutRef.current);
      loadingTimeoutRef.current = null;
    }
    setIsLoading(true);
    setShowFullScreenLoading(true);
    loadingStartTimeRef.current = Date.now();
  }, []);

  const stopLoading = useCallback(() => {
    if (loadingTimeoutRef.current) {
      clearTimeout(loadingTimeoutRef.current);
      loadingTimeoutRef.current = null;
    }

    ensureMinLoadingTime(() => {
      setIsLoading(false);
      setShowFullScreenLoading(false);
      loadingStartTimeRef.current = null;
    });
  }, [ensureMinLoadingTime]);

  const showLoadingScreen = useCallback((loading: boolean) => {
    if (loadingTimeoutRef.current) {
      clearTimeout(loadingTimeoutRef.current);
      loadingTimeoutRef.current = null;
    }

    if (loading) {
      setIsLoading(true);
      setShowFullScreenLoading(true);
      loadingStartTimeRef.current = Date.now();
    } else {
      ensureMinLoadingTime(() => {
        setIsLoading(false);
        setShowFullScreenLoading(false);
        loadingStartTimeRef.current = null;
      });
    }
  }, [ensureMinLoadingTime]);

  const setLoadingWithDelay = useCallback((loading: boolean, delay = 300) => {
    if (loadingTimeoutRef.current) {
      clearTimeout(loadingTimeoutRef.current);
      loadingTimeoutRef.current = null;
    }

    if (loading) {
      setIsLoading(true);
      setShowFullScreenLoading(true);
      loadingStartTimeRef.current = Date.now();
    } else {
      ensureMinLoadingTime(() => {
        loadingTimeoutRef.current = setTimeout(() => {
          setIsLoading(false);
          setShowFullScreenLoading(false);
          loadingTimeoutRef.current = null;
          loadingStartTimeRef.current = null;
        }, delay);
      });
    }
  }, [ensureMinLoadingTime]);

  return (
    <LoadingContext.Provider value={{ isLoading, startLoading, stopLoading, showLoadingScreen, setLoadingWithDelay }}>
      {showFullScreenLoading && isLoading && <LoadingScreen fullscreen={true} />}
      {children}
    </LoadingContext.Provider>
  );
};

// Komponent pomocniczy do ładowania w konkretnym miejscu (nie na całym ekranie)
export const LoadingArea = ({ size = 'md' }: { size?: 'sm' | 'md' | 'lg' }) => {
  const heightClasses = {
    sm: 'h-32',
    md: 'h-64',
    lg: 'h-96',
  };

  return (
    <div className={`w-full ${heightClasses[size]} flex justify-center items-center`}>
      <LoadingScreen fullscreen={false} />
    </div>
  );
};

export default LoadingProvider;
